/**
 * @fileoverview Cross-version ECDSA → DER normaliser for transparent signing.
 *
 * Zcash transparent (P2PKH) inputs are signed with secp256k1 ECDSA and the
 * scriptSig carries a DER-encoded signature followed by a one-byte sighash
 * type. The DER serialisation moved between @noble/curves majors, and getting
 * it wrong silently breaks the shield-and-send (auto-shield) mint path, so this
 * is isolated and unit-tested:
 *
 *   - v1 `secp256k1.sign()` returned a `Signature` object with `toDERRawBytes()`.
 *   - v2 `secp256k1.sign()` returns a raw 64-byte *compact* `Uint8Array` (r‖s);
 *     DER encoding now lives on `secp256k1.Signature`
 *     (`fromBytes(bytes, 'compact').toBytes('der')`).
 *
 * `secp` is passed in (rather than imported) so this stays a pure, dependency-
 * free helper and matches the project's "pass things through parameters" rule.
 */

/**
 * A minimal view of the bits of `@noble/curves` secp256k1 we rely on. The
 * `format` arguments are intentionally `any`: noble types them as a literal
 * union that differs between majors, and widening here keeps the real `ECDSA`
 * object structurally assignable without coupling us to a specific version.
 */
export interface Secp256k1Like {
	Signature?: {
		fromBytes?: (bytes: Uint8Array, format: any) => { toBytes: (format: any) => Uint8Array }
	}
}

/**
 * Normalise whatever `secp256k1.sign()` returned (across @noble/curves majors)
 * to canonical DER signature bytes. The caller appends the sighash-type byte.
 *
 * @throws if the signature shape can't be DER-encoded with the given `secp`.
 */
export function ecdsaSignatureToDer(secp: Secp256k1Like, signResult: unknown): Uint8Array {
	// v1: Signature object with a direct DER serialiser.
	if (signResult && typeof (signResult as { toDERRawBytes?: unknown }).toDERRawBytes === 'function') {
		return (signResult as { toDERRawBytes: () => Uint8Array }).toDERRawBytes()
	}
	// v2: raw compact (64-byte) Uint8Array → DER via the Signature class.
	if (signResult instanceof Uint8Array) {
		const Sig = secp?.Signature
		if (Sig && typeof Sig.fromBytes === 'function') {
			return Sig.fromBytes(signResult, 'compact').toBytes('der')
		}
		throw new Error('secp256k1.Signature.fromBytes unavailable for DER encoding')
	}
	// v2 Signature object (e.g. when a non-default format was requested).
	if (signResult && typeof (signResult as { toBytes?: unknown }).toBytes === 'function') {
		return (signResult as { toBytes: (format: string) => Uint8Array }).toBytes('der')
	}
	throw new Error('Unsupported secp256k1 signature shape — cannot DER-encode')
}
